import React from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { 
  Bot, Sparkles, MessageSquare, Zap, ArrowRight, Check,
  Users, Calendar, FileText, DollarSign, TrendingUp,
  Clock, Shield, Brain, Mic, BarChart3
} from 'lucide-react';

const AIAssistantPage = () => {
  const capabilities = [
    {
      icon: MessageSquare,
      title: "Natural Language Queries",
      description: "Ask questions in plain English and get instant answers about your business.",
      examples: [
        "How many jobs are scheduled for today?",
        "What's our total revenue this month?",
        "Show me all unpaid invoices",
        "Which customers haven't been serviced in 30 days?"
      ]
    },
    {
      icon: Users,
      title: "Crew Management",
      description: "Get recommendations and assign crews to jobs with simple commands.",
      examples: [
        "Which crew should handle the Johnson job?",
        "Assign Team Alpha to today's lawn maintenance jobs",
        "Show me crew availability for next week",
        "Unassign the crew from the cancelled job"
      ]
    },
    {
      icon: Calendar,
      title: "Job Scheduling",
      description: "Create, modify, and manage jobs through natural conversation.",
      examples: [
        "Schedule a lawn maintenance job for 123 Oak St tomorrow at 9am",
        "Mark the Wilson property job as completed",
        "What jobs are in progress right now?",
        "Reschedule Friday's jobs to Monday"
      ]
    },
    {
      icon: FileText,
      title: "Estimates & Proposals",
      description: "Create and send professional estimates without leaving the conversation.",
      examples: [
        "Create an estimate for $450 for lawn service at 123 Oak St",
        "Send the Johnson estimate to the customer",
        "What estimates are pending approval?",
        "Create a proposal for monthly maintenance at $200/month"
      ]
    },
    {
      icon: TrendingUp,
      title: "Business Insights",
      description: "Get real-time analytics and insights about your operations.",
      examples: [
        "What's our average job value this month?",
        "Which crew has the most completed jobs?",
        "Show me revenue trends for the past 6 months",
        "What's our customer retention rate?"
      ]
    },
    {
      icon: DollarSign,
      title: "Financial Overview",
      description: "Track payments, invoices, and financial health at a glance.",
      examples: [
        "How much revenue did we collect this week?",
        "Which invoices are overdue?",
        "What's our outstanding balance?",
        "Show me top 5 customers by revenue"
      ]
    }
  ];

  const useCases = [
    {
      title: "Morning Briefing",
      scenario: "Start your day with a quick overview",
      conversation: [
        { role: "user", text: "What do I need to know about today?" },
        { role: "ai", text: "Good morning! Here's your daily briefing:\n\n• 8 jobs scheduled today\n• 2 estimates pending approval ($1,450 total)\n• 3 invoices overdue ($890)\n• Team Alpha and Team Bravo are assigned\n• Weather: Clear, 75°F - great conditions!" }
      ]
    },
    {
      title: "Quick Crew Assignment",
      scenario: "Efficiently assign crews to optimize routes",
      conversation: [
        { role: "user", text: "Which crew should I assign to the north side jobs?" },
        { role: "ai", text: "Based on location and current workload, I recommend Team Alpha for the north side jobs:\n\n• They're currently closest (2 miles away)\n• Have capacity for 4 more jobs today\n• Familiar with 3 of the 4 properties\n\nWant me to assign them?" },
        { role: "user", text: "Yes, assign them" },
        { role: "ai", text: "✅ Done! Team Alpha assigned to 4 north side jobs. They've been notified and routes are optimized." }
      ]
    },
    {
      title: "On-the-Spot Estimates",
      scenario: "Create estimates while on a sales call",
      conversation: [
        { role: "user", text: "Create an estimate for a new customer: Sarah Johnson at 456 Maple Drive, weekly lawn service for $85" },
        { role: "ai", text: "I'll create that estimate:\n\n• Customer: Sarah Johnson (new)\n• Property: 456 Maple Drive\n• Service: Weekly lawn service\n• Price: $85/week\n\nShould I create this and send it to the customer?" },
        { role: "user", text: "Yes, create and send it" },
        { role: "ai", text: "✅ Done!\n• Created customer profile for Sarah Johnson\n• Added property at 456 Maple Drive\n• Created estimate #EST-2026-0047 for $85/week\n• Sent to sarah.johnson@email.com\n\nShe'll receive the approval link shortly." }
      ]
    }
  ];

  return (
    <MarketingLayout>
      {/* Hero Section */}
      <section className="pt-16 pb-24 bg-gradient-to-br from-violet-950 via-indigo-950 to-slate-900 text-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-violet-500/20 text-violet-300 px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Sparkles className="h-4 w-4" />
                AI-Powered Feature
              </div>
              <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
                Meet Your AI Business Assistant
              </h1>
              <p className="text-xl text-gray-300 mb-8">
                The most powerful way to manage your landscaping business. Ask questions, get insights, and take action—all through simple conversation. It's like having a smart assistant who knows everything about your operations.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/signup">
                  <Button size="lg" className="bg-violet-600 hover:bg-violet-700 text-white">
                    Try It Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
                <Link to="/pricing">
                  <Button size="lg" variant="outline" className="border-violet-400 text-violet-300 hover:bg-violet-900/50">
                    View Pricing
                  </Button>
                </Link>
              </div>
            </div>

            {/* Hero Chat Demo */}
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-violet-600/20 to-indigo-600/20 rounded-3xl blur-3xl"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 py-3 flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                    <Bot className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm">AI Assistant</h3>
                    <p className="text-emerald-100 text-xs">Always ready to help</p>
                  </div>
                  <div className="ml-auto flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                    <span className="text-emerald-100 text-xs">Online</span>
                  </div>
                </div>
                <div className="p-4 space-y-3 bg-gray-50 min-h-[280px]">
                  <div className="flex justify-end">
                    <div className="bg-emerald-600 text-white px-4 py-2 rounded-2xl rounded-br-md text-sm max-w-[80%]">
                      Give me a quick overview of the business
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="bg-white shadow-sm border border-gray-100 px-4 py-3 rounded-2xl rounded-bl-md text-sm max-w-[85%] text-gray-800">
                      <div className="flex items-start gap-2">
                        <Bot className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium mb-2">Here's your business snapshot:</p>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div className="bg-emerald-50 p-2 rounded">
                              <p className="text-gray-500">Today's Jobs</p>
                              <p className="font-bold text-emerald-700">8 scheduled</p>
                            </div>
                            <div className="bg-blue-50 p-2 rounded">
                              <p className="text-gray-500">Active Crews</p>
                              <p className="font-bold text-blue-700">3 in field</p>
                            </div>
                            <div className="bg-amber-50 p-2 rounded">
                              <p className="text-gray-500">Pending Estimates</p>
                              <p className="font-bold text-amber-700">$2,340</p>
                            </div>
                            <div className="bg-purple-50 p-2 rounded">
                              <p className="text-gray-500">This Week</p>
                              <p className="font-bold text-purple-700">$12,450</p>
                            </div>
                          </div>
                          <p className="mt-2 text-gray-500 text-xs">What would you like to dive into?</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Clock className="h-7 w-7 text-violet-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Save 10+ Hours/Week</h3>
              <p className="text-sm text-gray-600">Automate routine tasks and get answers instantly instead of digging through data.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Brain className="h-7 w-7 text-violet-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Smarter Decisions</h3>
              <p className="text-sm text-gray-600">AI-powered recommendations help you optimize routes, crews, and scheduling.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Zap className="h-7 w-7 text-violet-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Instant Actions</h3>
              <p className="text-sm text-gray-600">Create estimates, assign crews, and update jobs—all through simple commands.</p>
            </div>
            <div className="text-center">
              <div className="w-14 h-14 bg-violet-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Shield className="h-7 w-7 text-violet-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Always Confirms</h3>
              <p className="text-sm text-gray-600">AI always asks for confirmation before taking action—you stay in control.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">What Can The AI Assistant Do?</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              From simple queries to complex operations, your AI assistant handles it all through natural conversation.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {capabilities.map((cap, index) => (
              <Card key={index} className="border-gray-100 hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-violet-100 rounded-xl flex items-center justify-center mb-4">
                    <cap.icon className="h-6 w-6 text-violet-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{cap.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{cap.description}</p>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Example commands:</p>
                    {cap.examples.slice(0, 3).map((example, i) => (
                      <div key={i} className="flex items-start gap-2">
                        <MessageSquare className="h-3 w-3 text-violet-500 mt-1 flex-shrink-0" />
                        <span className="text-xs text-gray-600 italic">"{example}"</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Real Use Cases */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">See It In Action</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Real scenarios showing how the AI assistant helps you manage your business more efficiently.
            </p>
          </div>

          <div className="space-y-16">
            {useCases.map((useCase, index) => (
              <div key={index} className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="inline-flex items-center gap-2 bg-violet-100 text-violet-700 px-3 py-1 rounded-full text-sm font-medium mb-4">
                    Use Case {index + 1}
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{useCase.title}</h3>
                  <p className="text-lg text-gray-600 mb-6">{useCase.scenario}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Check className="h-4 w-4 text-emerald-500" />
                    <span>No training required - just type naturally</span>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-100">
                    <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 px-4 py-2 flex items-center gap-2">
                      <Bot className="h-4 w-4 text-white" />
                      <span className="text-white text-sm font-medium">AI Assistant</span>
                    </div>
                    <div className="p-4 space-y-3 bg-gray-50">
                      {useCase.conversation.map((msg, i) => (
                        <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[85%] px-4 py-2 rounded-2xl text-sm ${
                            msg.role === 'user' 
                              ? 'bg-emerald-600 text-white rounded-br-md' 
                              : 'bg-white shadow-sm border border-gray-100 rounded-bl-md text-gray-800'
                          }`}>
                            {msg.role === 'ai' && (
                              <div className="flex items-start gap-2">
                                <Bot className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                                <p className="whitespace-pre-line">{msg.text}</p>
                              </div>
                            )}
                            {msg.role === 'user' && msg.text}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gradient-to-br from-violet-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Getting started with the AI assistant is simple. No setup required.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            <div className="text-center">
              <div className="w-16 h-16 bg-violet-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                1
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Click the Chat Bubble</h3>
              <p className="text-gray-600 text-sm">Look for the green chat icon in the bottom right corner of any page.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-violet-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                2
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Ask or Command</h3>
              <p className="text-gray-600 text-sm">Type your question or what you want to do in plain English.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-violet-600 text-white rounded-2xl flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                3
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Get Results</h3>
              <p className="text-gray-600 text-sm">AI responds with answers or asks for confirmation before taking action.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-violet-900 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Sparkles className="h-12 w-12 text-violet-300 mx-auto mb-6" />
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to Work Smarter?
          </h2>
          <p className="text-xl text-violet-200 mb-8">
            Join landscaping companies already using AI to manage their operations more efficiently.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/signup">
              <Button size="lg" className="bg-white text-violet-900 hover:bg-gray-100">
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/contact">
              <Button size="lg" variant="outline" className="border-violet-400 text-white hover:bg-violet-800">
                Schedule Demo
              </Button>
            </Link>
          </div>
          <p className="text-sm text-violet-300 mt-6">
            No credit card required • 14-day free trial • Cancel anytime
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
};

export default AIAssistantPage;
